import { useState } from 'react';
import { User, Lock, Phone } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Button, Card, CardHeader, DetailRow, Field, Input, PageHeader, useToast,
} from '@/components/ui';
import { usePortal } from './PortalShell';

export default function PortalProfile() {
  const { data, reload } = usePortal();
  const { tenant } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    email: data.tenant.email ?? '',
    alt_phone: (data.tenant as any).alt_phone ?? '',
    occupation: data.tenant.occupation ?? '',
    employer: (data.tenant as any).employer ?? '',
    emergency_name: data.tenant.emergency_name ?? '',
    emergency_phone: data.tenant.emergency_phone ?? '',
    emergency_relation: data.tenant.emergency_relation ?? '',
  });

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function save() {
    setBusy(true);
    try {
      await api.patch('/portal/profile', form);
      toast.success('Details updated');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save your details');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Your details"
        subtitle="Keep your contact information up to date"
        action={<Button variant="primary" onClick={() => void save()} loading={busy}>Save changes</Button>}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={<span className="flex items-center gap-2"><Lock className="h-5 w-5" /> On file</span>}
            subtitle="Managed by your landlord"
          />
          <div className="p-5 sm:p-6 pt-4">
            <dl>
              <DetailRow label="Name">{data.tenant.full_name}</DetailRow>
              <DetailRow label="Phone">{tenant?.phone}</DetailRow>
              <DetailRow label="ID type">{data.tenant.id_type ?? '—'}</DetailRow>
              <DetailRow label="ID number">{data.tenant.id_number ?? '—'}</DetailRow>
            </dl>
            <p className="text-xs text-faint mt-4 leading-relaxed">
              Your phone number is how you sign in. To change your name, phone or ID details,
              contact your landlord — these are used on your tenancy agreement.
            </p>
          </div>
        </Card>

        <Card>
          <CardHeader
            title={<span className="flex items-center gap-2"><User className="h-5 w-5" /> Contact details</span>}
            subtitle="You can update these yourself"
          />
          <div className="p-5 sm:p-6 pt-4 space-y-4">
            <Field label="Email">
              <Input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </Field>
            <Field label="Alternative phone">
              <Input value={form.alt_phone} onChange={set('alt_phone')} placeholder="+254700000000" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Occupation">
                <Input value={form.occupation} onChange={set('occupation')} />
              </Field>
              <Field label="Employer">
                <Input value={form.employer} onChange={set('employer')} />
              </Field>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title={<span className="flex items-center gap-2"><Phone className="h-5 w-5" /> Emergency contact</span>}
            subtitle="Who should be called if something happens and you cannot be reached"
          />
          <div className="p-5 sm:p-6 pt-4 grid sm:grid-cols-3 gap-4">
            <Field label="Name">
              <Input value={form.emergency_name} onChange={set('emergency_name')} />
            </Field>
            <Field label="Phone">
              <Input value={form.emergency_phone} onChange={set('emergency_phone')} placeholder="+254700000000" />
            </Field>
            <Field label="Relationship">
              <Input value={form.emergency_relation} onChange={set('emergency_relation')} placeholder="Sibling" />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}
